import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { ToastComponent } from './shared/components/toast/toast.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastComponent],
  template: `<router-outlet /><app-toast />`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  private readonly swUpdate = inject(SwUpdate);
  private updateReady = false;

  constructor() {
    // Only active in production (service worker enabled). Keeps installed
    // mobile PWAs from getting stuck on an old build: we detect a new version
    // and reload — but only while the app is in the background, so a reload
    // never interrupts someone mid-transaction.
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => {
        this.updateReady = true;
        if (document.visibilityState === 'hidden') this.applyUpdate();
      });

    void this.swUpdate.checkForUpdate();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Safe moment: app just went to the background — swap to the new
        // version silently so it's ready next time the user returns.
        this.applyUpdate();
      } else {
        void this.swUpdate.checkForUpdate();
      }
    });

    // Installed PWAs may stay open for a long time without navigating, so poll.
    setInterval(() => void this.swUpdate.checkForUpdate(), 5 * 60 * 1000);
  }

  private applyUpdate(): void {
    if (!this.updateReady) return;
    this.updateReady = false;
    void this.swUpdate.activateUpdate().then(() => document.location.reload());
  }
}
